import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, CheckCircle2, AlertTriangle, User2, Mail, Phone, ShoppingBag, Sparkles, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import PriorityPill from "../../components/PriorityPill";
import SentimentDot from "../../components/SentimentDot";
import SLATimer from "../../components/SLATimer";
import { api } from "../../api/client";

const AdminSupportDetailContent = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const [resolveOpen, setResolveOpen] = useState(false);
    const [resolutionNotes, setResolutionNotes] = useState("");
    const [escalateOpen, setEscalateOpen] = useState(false);
    const [escalateReason, setEscalateReason] = useState("");
    const threadRef = useRef(null);

    const load = async () => {
        try {
            const { data: d } = await api.get(`/admin/support/tickets/${id}`);
            setData(d);
        } catch (err) {
            if (err?.response?.status === 404) {
                toast.error("Ticket not found");
                navigate("/admin/support");
            }
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [id]);

    useEffect(() => {
        if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }, [data?.messages?.length]);

    const send = async () => {
        const content = reply.trim();
        if (!content || sending) return;
        try {
            setSending(true);
            await api.post(`/admin/support/tickets/${id}/messages`, { content });
            setReply("");
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not send");
        } finally {
            setSending(false);
        }
    };

    const assignSelf = async () => {
        try {
            await api.post(`/admin/support/tickets/${id}/assign`, {});
            toast.success("Assigned to you");
            await load();
        } catch {
            toast.error("Assign failed");
        }
    };

    const submitResolve = async () => {
        if (!resolutionNotes.trim()) {
            toast.error("Add resolution notes");
            return;
        }
        try {
            await api.post(`/admin/support/tickets/${id}/resolve`, { resolution_notes: resolutionNotes.trim() });
            toast.success("Ticket resolved");
            setResolveOpen(false);
            setResolutionNotes("");
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not resolve");
        }
    };

    const submitEscalate = async () => {
        if (!escalateReason.trim()) {
            toast.error("Add escalation reason");
            return;
        }
        try {
            await api.post(`/admin/support/tickets/${id}/escalate`, { reason: escalateReason.trim() });
            toast.success("Escalated");
            setEscalateOpen(false);
            setEscalateReason("");
            await load();
        } catch {
            toast.error("Could not escalate");
        }
    };

    if (loading) return <div className="h-96 skeleton rounded-3xl" />;
    if (!data) return null;

    const t = data;
    const isFinal = ["resolved", "closed"].includes(t.status);

    return (
        <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
            {/* Thread */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-100 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5">
                                <span className="font-mono text-xs text-slate-400">{t.ticket_no}</span>
                                <PriorityPill priority={t.priority} />
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-700">{t.status.replace("_", " ")}</span>
                                <SentimentDot sentiment={t.sentiment} showLabel />
                            </div>
                            <h2 className="mt-1 line-clamp-1 text-base font-semibold text-slate-900">{t.subject}</h2>
                        </div>
                        <SLATimer slaDueAt={t.sla_due_at} slaBreached={t.sla_breached} status={t.status} />
                    </div>

                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {!t.assigned_to_admin && !isFinal && (
                            <button onClick={assignSelf} className="rounded-full bg-brand-700 px-3 py-1 text-xs font-medium text-white hover:bg-brand-800" data-testid="admin-support-assign-self">
                                Assign to me
                            </button>
                        )}
                        {!isFinal && (
                            <>
                                <button onClick={() => setResolveOpen(true)} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100" data-testid="admin-support-resolve">
                                    <CheckCircle2 className="h-3 w-3" /> Resolve
                                </button>
                                <button onClick={() => setEscalateOpen(true)} className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100" data-testid="admin-support-escalate">
                                    <AlertTriangle className="h-3 w-3" /> Escalate
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div ref={threadRef} className="h-[480px] space-y-2.5 overflow-y-auto bg-slate-50/40 p-4" data-testid="admin-support-messages">
                    {(t.messages || []).map((m) => {
                        const isAgent = m.sender_type === "agent";
                        const isSys = m.sender_type === "system";
                        if (isSys) {
                            return (
                                <div key={m.id} className="my-2 text-center">
                                    <span className="rounded-full bg-slate-200 px-3 py-0.5 text-[10px] uppercase tracking-wider text-slate-600">{m.content}</span>
                                </div>
                            );
                        }
                        return (
                            <div key={m.id} className={`flex ${isAgent ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                                    isAgent ? "bg-brand-700 text-white" : "bg-white text-slate-900 ring-1 ring-slate-100"
                                }`}>
                                    <p className="whitespace-pre-wrap break-words">{m.content}</p>
                                    <p className={`mt-1 text-[10px] ${isAgent ? "text-white/70" : "text-slate-400"}`}>
                                        {m.sender_name} · {new Date(m.created_at).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {!isFinal && (
                    <div className="border-t border-slate-100 p-3">
                        <div className="flex items-end gap-2">
                            <textarea
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }}
                                placeholder="Reply to the customer… (Ctrl+Enter to send)"
                                rows={2}
                                maxLength={4000}
                                className="flex-1 resize-none rounded-2xl border border-slate-200 p-3 text-sm outline-none focus:border-brand-500"
                                data-testid="admin-support-reply-input"
                            />
                            <button onClick={send} disabled={!reply.trim() || sending} className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-700 text-white hover:bg-brand-800 disabled:opacity-50" data-testid="admin-support-send-btn" aria-label="Send">
                                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Context panel */}
            <aside className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="admin-support-customer-card">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Customer</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{t.customer?.full_name || "—"}</p>
                    <p className="text-xs text-slate-500"><Mail className="mr-1 inline h-3 w-3" /> {t.customer?.email}</p>
                    {t.customer?.phone && <p className="text-xs text-slate-500"><Phone className="mr-1 inline h-3 w-3" /> {t.customer.phone}</p>}
                    {t.elite_member && (
                        <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                            <Sparkles className="h-3 w-3" /> Elite
                        </span>
                    )}
                    {t.customer?.id && (
                        <Link to={`/admin/users/${t.customer.id}`} className="mt-3 inline-block text-xs text-brand-700 hover:underline">
                            View full profile →
                        </Link>
                    )}
                </div>

                {t.sentiment && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="admin-support-sentiment-card">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">AI sentiment</p>
                        <div className="mt-1 flex items-center gap-2">
                            <SentimentDot sentiment={t.sentiment} size="lg" />
                            <span className="text-sm font-semibold capitalize text-slate-900">{t.sentiment.label}</span>
                            <span className="text-xs text-slate-500">· score {t.sentiment.score}/5</span>
                        </div>
                        {t.sentiment.reasoning && <p className="mt-2 text-xs text-slate-600">{t.sentiment.reasoning}</p>}
                        {(t.sentiment.keywords || []).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                                {t.sentiment.keywords.map((k, i) => (
                                    <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">{k}</span>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {t.linked_detail?.order && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="admin-support-order-card">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Linked order</p>
                        <p className="mt-1 font-mono text-sm text-slate-900">{t.linked_detail.order.order_no}</p>
                        <p className="text-xs text-slate-500">{t.linked_detail.order.status} · ₹{t.linked_detail.order.amounts?.total}</p>
                        <Link to={`/admin/orders/${t.linked_detail.order.id}`} className="mt-2 inline-block text-xs text-brand-700 hover:underline">
                            Open order →
                        </Link>
                    </div>
                )}
                {t.linked_detail?.prescription && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="admin-support-rx-card">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Linked prescription</p>
                        <p className="mt-1 text-sm text-slate-700">{t.linked_detail.prescription.verification_status}</p>
                        <Link to={`/admin/prescriptions/${t.linked_detail.prescription.id}`} className="mt-2 inline-block text-xs text-brand-700 hover:underline">
                            Review Rx →
                        </Link>
                    </div>
                )}

                {t.rating && (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Customer rating</p>
                        <p className="mt-1 text-lg font-bold text-emerald-900">{t.rating}/5</p>
                        {t.rating_feedback && <p className="mt-1 text-xs italic text-emerald-800">"{t.rating_feedback}"</p>}
                    </div>
                )}
            </aside>

            {/* Resolve modal */}
            {resolveOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-4" data-testid="admin-support-resolve-modal">
                    <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
                        <div className="flex items-center justify-between">
                            <h3 className="text-base font-semibold">Resolve ticket</h3>
                            <button onClick={() => setResolveOpen(false)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">Customer will be notified and asked to rate.</p>
                        <textarea
                            value={resolutionNotes}
                            onChange={(e) => setResolutionNotes(e.target.value)}
                            rows={4}
                            placeholder="Resolution notes (will be visible to the customer)…"
                            className="mt-3 w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-brand-500"
                            data-testid="admin-resolve-notes-input"
                        />
                        <div className="mt-3 flex justify-end gap-2">
                            <button onClick={() => setResolveOpen(false)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                            <button onClick={submitResolve} className="rounded-full bg-emerald-600 px-5 py-2 text-sm text-white hover:bg-emerald-700" data-testid="admin-resolve-submit">
                                Mark resolved
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Escalate modal */}
            {escalateOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-4" data-testid="admin-support-escalate-modal">
                    <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
                        <div className="flex items-center justify-between">
                            <h3 className="text-base font-semibold">Escalate ticket</h3>
                            <button onClick={() => setEscalateOpen(false)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">Priority will be bumped up. Reason is logged in audit + thread.</p>
                        <textarea
                            value={escalateReason}
                            onChange={(e) => setEscalateReason(e.target.value)}
                            rows={3}
                            placeholder="Why is this being escalated?"
                            className="mt-3 w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-brand-500"
                            data-testid="admin-escalate-reason-input"
                        />
                        <div className="mt-3 flex justify-end gap-2">
                            <button onClick={() => setEscalateOpen(false)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                            <button onClick={submitEscalate} className="rounded-full bg-rose-600 px-5 py-2 text-sm text-white hover:bg-rose-700" data-testid="admin-escalate-submit">
                                Escalate
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AdminSupportDetail = () => (
    <AdminAuthGuard>
        <AdminLayout title="Ticket" subtitle="Support thread" action={
            <Link to="/admin/support" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50">
                <ArrowLeft className="h-3 w-3" /> Back
            </Link>
        }>
            <AdminSupportDetailContent />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminSupportDetail;
